/**
 * Softphone — floating widget for in-browser calls via Telzio + JsSIP.
 *
 * - Mounted globally in InternalDashboard so it persists across pages.
 * - Auto-checks Telzio connection; only initializes when connected.
 * - Listens for window event `ventas:softphone:call` ({ detail: { e164, label } })
 *   so any "Call" button across the app can dispatch and we'll dial.
 * - Falls back gracefully if user hasn't connected Telzio.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Phone, PhoneOff, PhoneIncoming, MicOff, Mic, X, Loader2, ChevronUp, ChevronDown, Wifi, WifiOff,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';
import useTelzioSoftphone from '../hooks/useTelzioSoftphone';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDur = (s) => {
  if (!s) return '0:00';
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const Softphone = () => {
  const [telzioConnected, setTelzioConnected] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [dialNumber, setDialNumber] = useState('');
  const lastCallRef = useRef({ e164: null, label: null, at: 0 });

  const sp = useTelzioSoftphone();

  // Check Telzio connection on mount
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await authFetch(`${API}/api/telzio/status`);
        if (!res.ok || !active) return;
        const data = await res.json();
        if (active) setTelzioConnected(!!data.connected);
      } catch { /* */ }
    })();
    return () => { active = false; };
  }, []);

  // Auto-connect when Telzio is connected (one-shot)
  useEffect(() => {
    if (telzioConnected && sp.phase === 'idle') {
      sp.connect();
    }
  }, [telzioConnected, sp]);

  // Show error toasts
  useEffect(() => {
    if (sp.error) toast.error('Softphone', { description: sp.error });
  }, [sp.error]);

  // Auto-expand on incoming call or active call
  useEffect(() => {
    if (sp.phase === 'ringing' || sp.phase === 'in-call') setCollapsed(false);
  }, [sp.phase]);

  const handleGlobalCall = useCallback((evt) => {
    const { e164, label } = evt.detail || {};
    if (!e164) return;
    // De-dupe rapid double-fires (the link's default tel: navigation can trigger us twice)
    const now = Date.now();
    if (lastCallRef.current.e164 === e164 && now - lastCallRef.current.at < 1500) return;
    lastCallRef.current = { e164, label, at: now };

    if (sp.phase === 'in-call' || sp.phase === 'ringing') {
      toast.error('Already on a call');
      return;
    }
    if (!telzioConnected) {
      toast.error('Connect Telzio first', { description: 'Go to Calls & Voicemail and click "Connect Telzio"' });
      return;
    }
    if (sp.phase === 'connecting') {
      toast.info('Softphone is starting up', { description: 'Try again in a moment' });
      return;
    }
    if (sp.phase !== 'registered') {
      toast.error('Softphone not registered', { description: sp.error || 'Reconnect Telzio' });
      return;
    }
    setCollapsed(false);
    sp.call(e164, label);
  }, [sp, telzioConnected]);

  useEffect(() => {
    window.addEventListener('ventas:softphone:call', handleGlobalCall);
    return () => window.removeEventListener('ventas:softphone:call', handleGlobalCall);
  }, [handleGlobalCall]);

  // Don't render if user hasn't connected Telzio
  if (!telzioConnected) return null;

  return (
    <>
      {/* Incoming call modal */}
      {sp.incomingSession && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center" data-testid="softphone-incoming">
          <div className="bg-surface-card rounded-2xl shadow-2xl p-8 w-[400px] text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-brand/10 flex items-center justify-center mb-4 animate-pulse">
              <PhoneIncoming size={36} className="text-brand" />
            </div>
            <p className="text-xs uppercase tracking-wider text-text-tertiary mb-1">Incoming call</p>
            <h2 className="text-2xl font-bold text-text-primary mb-1">
              {sp.incomingMeta?.peerLabel || 'Unknown'}
            </h2>
            {sp.incomingMeta?.peerE164 && (
              <p className="text-sm text-text-secondary mb-6">{sp.incomingMeta.peerE164}</p>
            )}
            <div className="flex gap-3 justify-center">
              <Button
                onClick={sp.reject}
                className="bg-red-500 hover:bg-red-600 text-white px-6"
                data-testid="softphone-reject"
              >
                <PhoneOff size={18} className="mr-2" /> Decline
              </Button>
              <Button
                onClick={sp.answer}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
                data-testid="softphone-answer"
              >
                <Phone size={18} className="mr-2" /> Answer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating widget (bottom-right) */}
      <div
        className="fixed bottom-4 right-4 z-50 w-80 bg-surface-card border border-border-default rounded-2xl shadow-xl overflow-hidden"
        data-testid="softphone-widget"
      >
        {/* Header */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center gap-2 px-4 py-2.5 bg-text-primary text-white hover:bg-text-primary/90"
          data-testid="softphone-toggle"
        >
          {sp.phase === 'in-call' ? (
            <Phone size={14} className="text-emerald-400" />
          ) : sp.phase === 'registered' ? (
            <Wifi size={14} className="text-emerald-400" />
          ) : sp.phase === 'connecting' ? (
            <Loader2 size={14} className="animate-spin text-brand" />
          ) : (
            <WifiOff size={14} className="text-text-muted" />
          )}
          <span className="text-sm font-medium flex-1 text-left">
            {sp.phase === 'in-call' && sp.currentCall ? `On call · ${fmtDur(sp.currentCall.durationSec)}` : 'Softphone'}
          </span>
          <span className="text-[10px] uppercase tracking-wider opacity-70">
            {sp.phase === 'registered' ? 'Ready' : sp.phase === 'connecting' ? 'Starting' : sp.phase === 'error' ? 'Error' : sp.phase === 'in-call' ? 'Live' : sp.phase === 'ringing' ? 'Ringing' : 'Off'}
          </span>
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {!collapsed && (
          <div className="p-4 space-y-3">
            {sp.phase === 'in-call' && sp.currentCall ? (
              // ── Active call panel ──────────────────────────────────────
              <div className="space-y-3">
                <div className="text-center py-2">
                  <p className="text-xs uppercase tracking-wider text-text-tertiary mb-1">
                    {sp.currentCall.direction === 'inbound' ? 'Connected with' : 'Calling'}
                  </p>
                  <p className="text-lg font-semibold text-text-primary">{sp.currentCall.peerLabel}</p>
                  {sp.currentCall.peerE164 && (
                    <p className="text-xs text-text-tertiary">{sp.currentCall.peerE164}</p>
                  )}
                  <p className="text-2xl font-mono font-bold text-brand mt-2 tabular-nums">{fmtDur(sp.currentCall.durationSec)}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={sp.toggleMute}
                    className={`flex-1 ${sp.currentCall.muted ? 'bg-amber-50 border-amber-300 text-amber-700' : ''}`}
                    data-testid="softphone-mute"
                  >
                    {sp.currentCall.muted ? <MicOff size={14} className="mr-1" /> : <Mic size={14} className="mr-1" />}
                    {sp.currentCall.muted ? 'Unmute' : 'Mute'}
                  </Button>
                  <Button
                    onClick={sp.hangup}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                    data-testid="softphone-hangup"
                  >
                    <PhoneOff size={14} className="mr-1" /> End
                  </Button>
                </div>
                {/* DTMF keypad */}
                <div className="grid grid-cols-3 gap-1 pt-2 border-t border-border-subtle">
                  {['1','2','3','4','5','6','7','8','9','*','0','#'].map((d) => (
                    <button
                      key={d}
                      onClick={() => sp.sendDtmf(d)}
                      className="py-2 text-sm font-semibold text-text-secondary hover:bg-surface-page rounded"
                      data-testid={`softphone-dtmf-${d}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            ) : sp.phase === 'ringing' ? (
              // ── Outgoing dialing ──────────────────────────────────────
              <div className="text-center py-4">
                <Loader2 size={24} className="animate-spin mx-auto text-brand mb-2" />
                <p className="text-sm text-text-secondary">Ringing…</p>
                <Button
                  onClick={sp.hangup}
                  variant="outline"
                  className="mt-3 text-red-600 border-red-200"
                  data-testid="softphone-cancel"
                >
                  Cancel
                </Button>
              </div>
            ) : sp.phase === 'error' ? (
              <div className="text-center py-4 space-y-2">
                <p className="text-sm text-red-600">{sp.error || 'Softphone error'}</p>
                <Button onClick={sp.connect} variant="outline" data-testid="softphone-reconnect">
                  Reconnect
                </Button>
              </div>
            ) : sp.phase === 'connecting' ? (
              <div className="text-center py-4">
                <Loader2 size={20} className="animate-spin mx-auto text-brand" />
                <p className="text-xs text-text-tertiary mt-2">Connecting to Telzio…</p>
              </div>
            ) : (
              // ── Idle / dial panel ─────────────────────────────────────
              <form
                onSubmit={(ev) => {
                  ev.preventDefault();
                  if (dialNumber.trim()) {
                    sp.call(dialNumber.trim());
                    setDialNumber('');
                  }
                }}
                className="space-y-2"
              >
                <input
                  type="tel"
                  value={dialNumber}
                  onChange={(e) => setDialNumber(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="w-full px-3 py-2 border border-border-default rounded-md text-sm focus:outline-none focus:border-brand"
                  data-testid="softphone-dial-input"
                />
                <Button
                  type="submit"
                  disabled={!dialNumber.trim() || sp.phase !== 'registered'}
                  className="w-full bg-brand hover:bg-brand-hover"
                  data-testid="softphone-dial-btn"
                >
                  <Phone size={14} className="mr-1" /> Call
                </Button>
                {sp.phase !== 'registered' && (
                  <p className="text-[11px] text-text-tertiary text-center">
                    Status: {sp.phase}
                  </p>
                )}
              </form>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Softphone;
